import {
	Outlet,
	useLocation,
	useMatches,
	useNavigate,
	useNavigation,
	useRevalidator,
} from 'react-router';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import MainDrawer from 'src/components/ui/MainDrawer';
import DashboardLayout from 'src/components/ui/DashboardLayout';
import Header from 'src/components/ui/Header';
import CompanySideArea from 'src/components/CompanySideArea';
import DashboardNavigationCMenu from 'src/components/ui/DashboardNavigation/connectors/DashboardNavigationCMenu';
import DrawerContent from 'src/components/ui/DrawerContent';
import NavUser from 'src/components/ui/NavUser';
import Loader from 'src/components/ui/Loader';
import { GET_ME } from 'src/api/graphql/queries/me.gql';
import { mapMeToNavUser } from 'src/utils/mappers/user.mapper';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { getRoutePageTitle } from 'src/utils/helpers/documentTitle';
import { buildNoAccessPath } from './constants';
import { isUnauthorizedError } from 'src/utils/helpers/isUnauthorizedError';

const MainLayout = () => {
	const location = useLocation();
	const matches = useMatches();
	const navigation = useNavigation();
	const revalidator = useRevalidator();
	const navigate = useNavigate();
	const { data, error: meError } = useQuery(GET_ME);
	const isRouteLoading =
		navigation.state === 'loading' || revalidator.state === 'loading';

	useEffect(() => {
		if (isUnauthorizedError(meError)) {
			navigate(buildNoAccessPath(location), { replace: true });
		}
	}, [location, meError, navigate]);

	const companyMatch = matches.find((route) => route.id === 'company');
	const company = companyMatch?.data?.company;
	const pageTitle = useMemo(
		() =>
			getRoutePageTitle({
				pathname: location.pathname,
				company,
			}),
		[company, location.pathname],
	);

	useDocumentTitle(pageTitle);
	const companyDataKey = [
		company?.updatedAt,
		company?.statusAt,
		company?.params?.updatedAt,
		company?.status?.id,
	].join('-');

	const headerUser = null;
	const navUser = mapMeToNavUser(data?.me);

	const renderHeader = () => (
		<Header
			enableFullscreenBtn={false}
			enableCustomizationBtn={false}
			user={headerUser}
		/>
	);

	const renderNavigation = () => (
		<DashboardNavigationCMenu selectedId="commission" />
	);

	const renderNavUser = () => <NavUser {...(navUser ?? {})} />;

	const renderDrawerContent = () => (
		<DrawerContent
			NavigationRP={renderNavigation}
			NavUserRP={renderNavUser}
		/>
	);

	const renderMainDrawer = () => (
		<MainDrawer DrawerContentRP={renderDrawerContent} />
	);

	return (
		<DashboardLayout
			HeaderRP={renderHeader}
			MainDrawerRP={renderMainDrawer}
			// TODO: fix location check
			innerAside={
				location.pathname.includes('/company/') && (
					<CompanySideArea key={companyDataKey} company={company} />
				)
			}
		>
			<Loader open={isRouteLoading} overlay />
			<Outlet />
		</DashboardLayout>
	);
};

export default MainLayout;
